import { ClientEvents } from 'common/events/ClientEvents';
import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import React, { useEffect } from 'react';
import { RouteChildrenProps } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { ChatProvider } from 'src/chat/ChatProvider';
import { IntercomProvider } from 'src/intercom/IntercomProvider';
import { useNativeApp } from 'src/mobile-app/hooks/useNativeApp';
import { CcpaConsentBanner } from 'src/pages/consumer/components/ccpa/CcpaConsentBanner';
import { CcpaCustomizeModal } from 'src/pages/consumer/components/ccpa/CcpaCustomizeModal';
import { DishFirstCartPositionProvider } from 'src/pages/consumer/components/ConsumerHeaderWrapper';
import { ConsumerRouter } from 'src/pages/consumer/ConsumerRouter';
import { PreCheckoutUpsellModalProvider } from 'src/pages/consumer/dish-first/explore/components/PreCheckoutUpsellModal';
import { useBrandFavicon } from 'src/shared/brand/useBrandFavicon';
import { HelpButton } from 'src/shared/design-system/HelpButton';
import { ToastContainer } from 'src/shared/design-system/Toast';
import { useSendUserInfoToGoogleTagManager } from 'src/shared/hooks/useSendUserInfoToGoogleTagManager';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useUtm } from 'src/shared/hooks/useUtm';
import { useGlobalReducer, useScopedReducer } from 'src/shef-global-state/shefGlobalState';
import { useFetchGlobalState } from 'src/shef-global-state/useFetchGlobalState';
import { ShortLinkRedirect } from 'src/short-links/ShortLinkRedirect';
import { SHORT_LINK_PATHS } from 'src/short-links/ShortLinks';
import { useDispatch } from 'src/store';
import { HistoryProvider } from 'src/util/history';
import { lazyRetry } from 'src/util/retry';
import { StripeProvider } from 'src/vendor/stripe/StripeProvider';
import { ScreenContextProvider } from 'web-common/src/shared/context/ScreenContext/Provider';
import { LoadingScreen } from 'web-common/src/shared/design-system/components/LoadingScreen';
import { Debugger } from './experimentation/ExperimentDebugger';
import { NativeAppContext } from './mobile-app/hooks/useNativeAppContext';
import { GlobalImpersonationHeader } from './pages/components/GlobalImpersonationHeader';
import { BulkDiscountModalProvider } from './pages/consumer/dish-first/explore/components/BulkDiscountModal';
import { useExperiment } from './shared/hooks/experiments/useExperiment';
import { useApiSync } from './shared/hooks/useApiSyncHook';
import { useScrollToAnchor } from './shared/hooks/useScrollToAnchor';
import { useScrollToTopOnHistoryChange } from './shared/hooks/useScrollToTopOnHistoryChange';
import { Routes } from './shared/Routes';
import { isDevEnvironment, isPreviewEnvironment } from './shared/utils/EnvironmentUtilities';
import './styles/scss/app.scss';
import './styles/vendor/tailwind.css';
import { TrackedRouteWithErrorBoundary } from './tracking/tracked-route';

const ShefRouter = lazyRetry(() => import('./pages/shef/ShefRouter'));
const AdminRouter = lazyRetry(() => import('./pages/admin/AdminRouter'));
const PhotoEditorRouter = lazyRetry(() => import('./pages/photo-editors/PhotoEditorRouter'));
const Logout = lazyRetry(() => import('./pages/Logout'));

interface IAppProps {
  match?: RouteChildrenProps['match'];
  history: RouteChildrenProps['history'];
  location: RouteChildrenProps['location'];
}

function App({ history, location }: IAppProps) {
  const { isTreatment: isIntakeOptimizationTreatment } = useExperiment(ExperimentNames.INTAKE_OPTIMIZATION);
  const { state } = useGlobalReducer();
  const { currentUser, isImpersonating } = state;
  const tracker = useTracker();
  const dispatch = useDispatch();
  useScrollToTopOnHistoryChange();
  useScrollToAnchor();
  useBrandFavicon();
  useFetchGlobalState({ dispatch }).catch(console.error);
  useUtm({ location });

  const { state: configs } = useScopedReducer('configs');
  const [needToUpdate] = useApiSync(history);

  useEffect(() => {
    if (!configs.apiSyncEnabled || !needToUpdate) {
      return;
    }

    tracker.track(ClientEvents.API_SYNC_RELOAD, {});
    window.location.reload();
  }, [configs.apiSyncEnabled, needToUpdate, tracker]);

  useSendUserInfoToGoogleTagManager();

  const nativeApp = useNativeApp();

  return (
    <ScreenContextProvider>
      <HistoryProvider history={history}>
        <StripeProvider>
          <NativeAppContext.Provider value={nativeApp}>
            <IntercomProvider>
              <ChatProvider>
                <DishFirstCartPositionProvider>
                  <BulkDiscountModalProvider>
                    <PreCheckoutUpsellModalProvider>
                      <ToastContainer autoClose={3000} hideProgressBar pauseOnHover closeOnClick />
                      {Boolean(isDevEnvironment() || isPreviewEnvironment() || currentUser?.admin) && <Debugger />}
                      {isImpersonating && <GlobalImpersonationHeader />}
                      <React.Suspense
                        fallback={
                          location.pathname === Routes.CONSUMER_MEAL_PLANS_PREFERENCES &&
                          isIntakeOptimizationTreatment ? null : (
                            <LoadingScreen />
                          )
                        }>
                        <Switch>
                          <TrackedRouteWithErrorBoundary path={Routes.LOGOUT} component={Logout} />
                          <Route path={Routes.PHOTO_EDITOR} component={PhotoEditorRouter} />
                          <Route path={Routes.ADMIN} component={AdminRouter} />
                          <Route path={Routes.SHEF} component={ShefRouter} />
                          <Route exact path={SHORT_LINK_PATHS} component={ShortLinkRedirect} />
                          <Route path={Routes.CONSUMER_LANDING} component={ConsumerRouter} />
                        </Switch>
                      </React.Suspense>
                      <HelpButton />
                      <CcpaConsentBanner />
                      <CcpaCustomizeModal />
                    </PreCheckoutUpsellModalProvider>
                  </BulkDiscountModalProvider>
                </DishFirstCartPositionProvider>
              </ChatProvider>
            </IntercomProvider>
          </NativeAppContext.Provider>
        </StripeProvider>
      </HistoryProvider>
    </ScreenContextProvider>
  );
}

export default App;
